import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

import {
	DashboardBlock,
	AdminDashboardCompanyInfo,
	AdminDashboardSiteInfo,
} from 'components/Dashboard';
import { AdminClientsList } from 'components/AdminClientsList';
import { AuthContext } from 'context/auth';
import ToastifyHandler from 'utils/ToastifyHandler';
import {
	CONFIGURATION_TYPE,
	CONFIGURATION_STATUS,
	CONFIGURATIONS_COLLECTION_NAME,
} from 'utils/constant';
import { firebaseDB } from 'utils/firebase';
import { Spinner } from 'components/Spinner';

const AdminDashboardPage = () => {
	const { t } = useTranslation(['commonAppValues', 'firebaseErrorMessages']);
	const { currentUserAdditionalData } = useContext(AuthContext);
	const notificationsHandler = useRef(new ToastifyHandler());

	const [allCompaniesConfigurations, setAllCompaniesConfigurations] = useState(null);

	const [companiesRequestsConfigurations, setCompaniesRequestsConfigurations] = useState(null);
	const [companiesOrdersConfigurations, setCompaniesOrdersConfigurations] = useState(null);
	const [companiesOpenRequestsConfigurations, setCompaniesOpenRequestsConfigurations] =
		useState(null);
	const [companiesOpenOrdersConfigurations, setCompaniesOpenOrdersConfigurations] = useState(null);

	const [numberOfConfigurationsByType, setNumberOfConfigurations] = useState(null);

	const subscribeToListenConfigurationsChanges = useRef(null);

	const listenCompanyConfigurationsChanges = () => {
		const companyConfigurationsRef = collection(firebaseDB, CONFIGURATIONS_COLLECTION_NAME);

		const queryCompanyCollection = query(companyConfigurationsRef, where('type', '!=', 'drafts'));

		subscribeToListenConfigurationsChanges.current = onSnapshot(
			queryCompanyCollection,
			querySnapshot => {
				const allConfigurations = [];

				querySnapshot.forEach(doc => {
					allConfigurations.push(doc.data());
				});

				setAllCompaniesConfigurations(allConfigurations);
			},
			error => {
				notificationsHandler.current.error(error.message);
			}
		);
	};

	useEffect(() => {
		listenCompanyConfigurationsChanges();
	}, []);

	useEffect(() => {
		const splitConfigurationsByType = () => {
			const openRequestsConfigurations = [];
			const requestsConfigurations = [];
			const openOrdersConfigurations = [];
			const ordersConfigurations = [];

			allCompaniesConfigurations.forEach(companyConfiguration => {
				if (companyConfiguration.adminVisibility === 'hidden') {
					return; // skip hidden configs (we can't filter)
				}
				switch (companyConfiguration.type) {
					case CONFIGURATION_TYPE.request:
						if (companyConfiguration.status === CONFIGURATION_STATUS.open) {
							openRequestsConfigurations.push(companyConfiguration);
						} else {
							requestsConfigurations.push(companyConfiguration);
						}

						break;

					case CONFIGURATION_TYPE.order:
						if (companyConfiguration.status === CONFIGURATION_STATUS.open) {
							openOrdersConfigurations.push(companyConfiguration);
						} else {
							ordersConfigurations.push(companyConfiguration);
						}
						break;

					default:
						// TODO: Replace price on drafts with current price
						break;
				}
			});

			setCompaniesOpenRequestsConfigurations(openRequestsConfigurations);
			setCompaniesRequestsConfigurations(requestsConfigurations);
			setCompaniesOpenOrdersConfigurations(openOrdersConfigurations);
			setCompaniesOrdersConfigurations(ordersConfigurations);

			setNumberOfConfigurations({
				openRequests: openRequestsConfigurations.length,
				requests: requestsConfigurations.length,
				openOrders: openOrdersConfigurations.length,
				orders: ordersConfigurations.length,
			});
		};

		if (allCompaniesConfigurations) {
			splitConfigurationsByType();
		}
	}, [allCompaniesConfigurations]);

	useEffect(() => {
		if (
			!companiesOpenRequestsConfigurations ||
			!companiesRequestsConfigurations ||
			!companiesOpenOrdersConfigurations ||
			!companiesOrdersConfigurations
		) {
			return;
		}
		if (
			companiesOpenRequestsConfigurations.length !== numberOfConfigurationsByType.openRequests ||
			companiesRequestsConfigurations.length !== numberOfConfigurationsByType.requests ||
			companiesOpenOrdersConfigurations.length !== numberOfConfigurationsByType.openOrders ||
			companiesOrdersConfigurations.length !== numberOfConfigurationsByType.orders
		) {
			setNumberOfConfigurations({
				openRequests: companiesOpenRequestsConfigurations.length,
				requests: companiesRequestsConfigurations.length,
				openOrders: companiesOpenOrdersConfigurations.length,
				orders: companiesOrdersConfigurations.length,
			});
		}
	}, [
		companiesOpenRequestsConfigurations,
		companiesRequestsConfigurations,
		companiesOpenOrdersConfigurations,
		companiesOrdersConfigurations,
		numberOfConfigurationsByType,
	]);

	const setTotalNumberClientsCompany = useCallback(
		totalNumberClientsCompany => {
			if (!numberOfConfigurationsByType) return;
			if (totalNumberClientsCompany === null || totalNumberClientsCompany === 'undefined') return;

			const clonedNumberOfConfigurationsByType = structuredClone(numberOfConfigurationsByType);
			clonedNumberOfConfigurationsByType.companies = totalNumberClientsCompany;
			setNumberOfConfigurations(clonedNumberOfConfigurationsByType);
		},
		[numberOfConfigurationsByType, setNumberOfConfigurations]
	);

	useEffect(() => {
		return () => {
			if (subscribeToListenConfigurationsChanges.current) {
				subscribeToListenConfigurationsChanges.current();
			}
		};
	}, []);

	const companyData = currentUserAdditionalData?.company;
	return companyData ? (
		<section className="section dashboard">
			<div className="section__in">
				<ul className="dashboard__list">
					<li className="dashboard__item">
						<AdminDashboardSiteInfo />
					</li>
					<li className="dashboard__item">
						<AdminDashboardCompanyInfo
							companyName={companyData.name}
							numberOfConfigurationsByType={numberOfConfigurationsByType}
						/>
					</li>
					{companiesOpenRequestsConfigurations && companiesOpenRequestsConfigurations.length ? (
						<li className="dashboard__item">
							<DashboardBlock
								type="requests"
								typeStatus="open"
								isAdmin
								titleLabel={t('openRequests')}
								configurationData={companiesOpenRequestsConfigurations}
								setConfigurationData={setCompaniesOpenRequestsConfigurations}
							/>
						</li>
					) : null}
					{companiesOpenOrdersConfigurations && companiesOpenOrdersConfigurations.length ? (
						<li className="dashboard__item">
							<DashboardBlock
								type="orders"
								typeStatus="open"
								isAdmin
								titleLabel={t('openOrders')}
								configurationData={companiesOpenOrdersConfigurations}
								setConfigurationData={setCompaniesOpenOrdersConfigurations}
							/>
						</li>
					) : null}
					{companiesRequestsConfigurations && companiesRequestsConfigurations.length ? (
						<li className="dashboard__item">
							<DashboardBlock
								type="requests"
								typeStatus="closed"
								isAdmin
								titleLabel={t('requests')}
								configurationData={companiesRequestsConfigurations}
								setConfigurationData={setCompaniesRequestsConfigurations}
							/>
						</li>
					) : null}
					{companiesOrdersConfigurations && companiesOrdersConfigurations.length ? (
						<li className="dashboard__item">
							<DashboardBlock
								type="orders"
								typeStatus="closed"
								isAdmin
								titleLabel={t('orders')}
								configurationData={companiesOrdersConfigurations}
								setConfigurationData={setCompaniesOrdersConfigurations}
							/>
						</li>
					) : null}
					<li className="dashboard__item">
						<AdminClientsList setTotalNumberClientsCompany={setTotalNumberClientsCompany} />
					</li>
				</ul>
			</div>
		</section>
	) : currentUserAdditionalData === null ? (
		<Spinner darkTheme />
	) : (
		<div>Missing user data</div>
	);
};

export default AdminDashboardPage;
